import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, Plus, Trash2, Loader2, User, Shield, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "./AuthContext";
import { formatDate } from "./statusConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamManagement() {
  const { user: currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("employee");
  const [creating, setCreating] = useState(false);

  // Change own password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/staff`, { withCredentials: true });
      setStaff(data);
    } catch {
      toast.error("Couldn't load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(
        `${API}/auth/staff`,
        { name: newName, email: newEmail, password: newPassword, role: newRole },
        { withCredentials: true }
      );
      toast.success(`${newName} can now sign in`);
      setNewName("");
      setNewEmail("");
      setNewPassword("");
      setNewRole("employee");
      setShowForm(false);
      fetchStaff();
    } catch (err) {
      toast.error("Couldn't create account", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (!window.confirm(`Remove ${staffMember.name}'s access?`)) return;
    try {
      await axios.delete(`${API}/auth/staff/${staffMember.id}`, { withCredentials: true });
      toast.success(`${staffMember.name} removed`);
      fetchStaff();
    } catch (err) {
      toast.error("Couldn't remove account", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangingPw(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPw, new_password: newPw },
        { withCredentials: true }
      );
      toast.success("Your password has been updated");
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      toast.error("Couldn't change password", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div data-testid="team-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Team</h1>
          <p className="text-gray-500 text-sm mt-1">Manage who can access your lead dashboard.</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-2.5 px-4 rounded-lg transition-colors text-sm"
          data-testid="add-staff-button"
        >
          <Plus className="w-4 h-4" />
          Add Team Member
        </button>
      </div>

      {/* Create staff form */}
      {showForm && (
        <form
          onSubmit={handleCreateStaff}
          className="bg-[#18181b] rounded-xl border border-[#27272a] p-6 mb-6"
          data-testid="create-staff-form"
        >
          <h2 className="text-lg font-bold text-white mb-4">New Team Member</h2>
          <div className="grid sm:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
              <input
                type="text"
                required
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Their name"
                className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="their@email.com"
                className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Temporary Password</label>
              <input
                type="text"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
                data-testid="staff-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
                data-testid="staff-role-select"
              >
                <option value="employee">Employee</option>
                <option value="supervisor">Supervisor</option>
                <option value="subcontractor">Subcontractor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-2.5 px-5 rounded-lg transition-colors text-sm disabled:opacity-50"
              data-testid="create-staff-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Account
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white bg-white/5 transition-colors"
              data-testid="cancel-staff-form"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Staff list */}
      <div className="bg-[#18181b] rounded-xl border border-[#27272a] overflow-hidden mb-8" data-testid="staff-list">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#F5C518] animate-spin" />
          </div>
        ) : (
          <div className="divide-y divide-[#27272a]">
            {staff.map((member) => (
              <div key={member.id} className="flex items-center justify-between px-6 py-4" data-testid={`staff-row-${member.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#F5C518]/10 border border-[#F5C518]/20 flex items-center justify-center">
                    {member.role === "admin" ? (
                      <Shield className="w-4 h-4 text-[#F5C518]" />
                    ) : member.role === "supervisor" ? (
                      <Shield className="w-4 h-4 text-[#A78BFA]" />
                    ) : (
                      <User className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {member.name}
                      {member.id === currentUser?.id && <span className="text-gray-500 text-xs ml-2">(you)</span>}
                    </p>
                    <p className="text-gray-500 text-sm">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
                    member.role === "admin"
                      ? "bg-[#F5C518]/10 text-[#F5C518] border border-[#F5C518]/20"
                      : member.role === "supervisor"
                      ? "bg-[#A78BFA]/10 text-[#A78BFA] border border-[#A78BFA]/20"
                      : "bg-white/5 text-gray-400 border border-[#27272a]"
                  }`}>
                    {member.role}
                  </span>
                  {member.role !== "admin" && (
                    <button
                      onClick={() => handleDelete(member)}
                      className="p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      data-testid={`delete-staff-${member.id}`}
                      aria-label={`Remove ${member.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change own password */}
      <div className="bg-[#18181b] rounded-xl border border-[#27272a] p-6 max-w-lg" data-testid="change-password-card">
        <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#F5C518]" />
          Change Your Password
        </h2>
        <p className="text-gray-500 text-xs mb-5">Use a strong password you don't reuse elsewhere.</p>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Current Password</label>
            <input
              type="password"
              required
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="Min 8 characters"
              className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none"
              data-testid="new-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={changingPw}
            className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-2.5 px-5 rounded-lg transition-colors text-sm disabled:opacity-50"
            data-testid="change-password-submit"
          >
            {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
}
